import { useDispatch, useSelector } from 'react-redux'
import { DocumentState } from '../../redux/types'
import { selectDocumentState, setUserWriterSelections } from '../../redux/docSlice'
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import { useEffect, useState } from 'react'
import Modal from '../Modals/Modal'
import { EditorContent, useEditor } from '@tiptap/react'
import Placeholder from '@tiptap/extension-placeholder'
import Button from '../Buttons'
import { LuAudioWaveform, LuBookOpen, LuBriefcase, LuGraduationCap, LuSparkles, LuUser } from 'react-icons/lu'
import { selectUser } from '../../redux/systemSlice'
import { useAtom } from 'jotai'
import { docLoadingAtom } from '../../Pages/Create/Create'
import { debounce } from 'lodash'
import { TbSparkles } from 'react-icons/tb'
import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react'
import { FEATURE_FLAGS } from '../../hooks/featureFlags'

const personas = [
	{
		id: 'Standard',
		title: 'Academic Scholar',
		description:
			'Formal, research-focused writing with scholarly tone, complex vocabulary, and citation-heavy approach.',
		icon: LuGraduationCap,
	},
	{
		id: 'professional',
		title: 'Business Professional',
		description: 'Clear, concise corporate communication style with actionable insights and professional terminology.',
		icon: LuBriefcase,
	},
	{
		id: 'creative',
		title: 'Creative Writer',
		description: 'Engaging, narrative-driven style with vivid imagery, storytelling elements, and emotional depth.',
		icon: LuBookOpen,
	},
	{
		id: 'conversational',
		title: 'Conversational Expert',
		description: 'Approachable, friendly tone that explains complex topics in accessible, everyday language.',
		icon: LuUser,
	},
]

const CustomWritingStyleModal = ({
	docID,
	modalOpen,
	setModalOpen,
}: {
	docID: string
	modalOpen: boolean
	setModalOpen: (open: boolean) => void
}) => {
	const [docLoading] = useAtom(docLoadingAtom)
	const [userWritingStyleSet, setUserWritingStyleSet] = useState(false)
	const [showResult, setShowResult] = useState(false)
	const [loading, setLoading] = useState(false)
	const editor = useEditor({
		extensions: [
			Document,
			Text,
			Paragraph,
			Placeholder.configure({
				placeholder: 'Paste your sample text here...',
			}),
		],
		content: '',
	})
	const posthog = usePostHog()

	const documentState: DocumentState = useSelector((state) => selectDocumentState(state, docID))
	const dispatch = useDispatch()
	const user = useSelector(selectUser)

	useEffect(() => {
		if (user.writingStyle && documentState?.userWriterSelections && !userWritingStyleSet && !docLoading && docID) {
			setShowResult(true)
			setUserWritingStyleSet(true)
			const debouncedTabKeyPress = debounce(() => {
				dispatch(
					setUserWriterSelections({
						docID: docID,
						userWriterSelections: {
							...documentState.userWriterSelections,
							writingStyle: {
								...documentState.userWriterSelections.writingStyle,
								description: user.writingStyle.shortDescription,
								longDescription: user.writingStyle.longDescription,
								sampleText: user.writingStyle.sampleText,
							},
						},
					})
				)
			}, 200)
			debouncedTabKeyPress()
		}
	}, [user.writingStyle, docID, dispatch, documentState?.userWriterSelections, userWritingStyleSet, docLoading, docID])

	const generateCustomWritingStyle = () => {
		posthog?.capture('generate-custom-writing-style')
		const requestOptions = {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				referenceText: editor?.getText(),
				descriptionText: undefined,
				user_id: user.id,
			}),
		}
		setLoading(true)

		fetch(process['env']['REACT_APP_API_ROOT'] + '/essay/generate-writing-style/', requestOptions)
			.then((res) => res.json())
			.then((response: { shortDescription: string; longDescription: string; sampleText: string }) => {
				setLoading(false)
				setShowResult(true)
				setModalOpen(false)

				const text = editor?.getText().split(' ').slice(0, 500).join(' ')
				dispatch(
					setUserWriterSelections({
						docID: docID,
						userWriterSelections: {
							writingStyle: {
								referenceText: text,
								description: response.shortDescription,
								longDescription: response.longDescription,
								sampleText: response.sampleText,
								generalSelected: false,
								name: 'Create & Edit',
							},
						},
					})
				)
			})
			.catch((err) => {
				console.error(err)
			})
	}

	return (
		<Modal
			open={modalOpen}
			closeModal={() => {
				setModalOpen(false)
				if (documentState.userWriterSelections.writingStyle?.description) {
					setShowResult(true)
				}
			}}
			portalClassName="customWritingStyle"
		>
			<div className="flex flex-col gap-4 mt-5">
				{showResult ? (
					<div className="flex flex-col gap-1">
						<div className="text-xl font-semibold text-center">
							Your custom writing style {LuSparkles({ className: 'inline-block ml-0.5 mb-1' })}
						</div>
						<div className="text-sm text-gray-800 text-center">
							Your custom writing style has been generated. Below, you'll find a description of your style along with a
							sample text that demonstrates it.
						</div>
					</div>
				) : (
					<div className="flex flex-col gap-1">
						<div className="text-xl font-semibold text-center">Create a Style</div>
						<div className="text-sm text-gray-800 text-center">
							Paste a sample of your writing below to create a personalized AI writing style that captures your unique
							voice and tone.
						</div>
					</div>
				)}
				<div className="flex flex-col gap-4">
					{showResult ? (
						<>
							<div className="h-1 border-b border-gray-400" />
							<div className="text-sm">
								<span className="font-semibold">Description:</span>{' '}
								{documentState.userWriterSelections.writingStyle?.description}
							</div>
							<div className="flex flex-col gap-2">
								<div className="text-sm font-semibold">Sample Text:</div>
								<div className="h-48 bg-gray-100 text-sm rounded border border-gray-400 p-3 overflow-y-auto">
									{documentState.userWriterSelections.writingStyle?.sampleText}
								</div>
							</div>
						</>
					) : (
						<>
							<div>
								<EditorContent
									editor={editor}
									id="custom-style-editor"
									className="border border-gray-500 rounded max-h-96 overflow-auto"
									placeholder="Paste your sample text here..."
								/>
								<div className="text-sm text-gray-700">Minimum 20 words</div>
							</div>
						</>
					)}
					{showResult ? (
						<Button
							type="outline"
							onClick={() => {
								setShowResult(false)
							}}
						>
							Edit Style
						</Button>
					) : (
						<Button
							type="default"
							disabled={(editor?.getText().split(' ').length ?? 0) < 20 || loading}
							onClick={() => {
								generateCustomWritingStyle()
							}}
						>
							{loading ? (
								'Thinking...'
							) : documentState.userWriterSelections.writingStyle.description ? (
								<span>Save style</span>
							) : (
								<span>Generate Style</span>
							)}
						</Button>
					)}
				</div>
			</div>
		</Modal>
	)
}

const WritingStyleSelection = ({ docID, omitHeader = false }: { docID: string; omitHeader?: boolean }) => {
	const [modalOpen, setModalOpen] = useState(false)
	const personasFF = useFeatureFlagVariantKey(FEATURE_FLAGS.PERSONAS)
	const documentState: DocumentState = useSelector((state) => selectDocumentState(state, docID))
	const dispatch = useDispatch()
	if (!documentState.userWriterSelections.writingStyle) {
		return null
	}
	const generalSelected =
		!!documentState.userWriterSelections.writingStyle?.generalSelected ||
		['academic', 'professional', 'creative', 'conversational'].includes(
			documentState.userWriterSelections.writingStyle?.name
		)
	const customWritingStyleGenerated =
		!!documentState.userWriterSelections.writingStyle?.longDescription && !generalSelected

	if (personasFF === 'test') {
		return (
			<>
				<CustomWritingStyleModal docID={docID} modalOpen={modalOpen} setModalOpen={setModalOpen} />
				<div
					className={`cursor-pointer border-2 px-6 py-4 rounded-lg flex flex-col items-start hover:bg-gray-50 mt-4 ${
						!generalSelected ? 'bg-gray-100 border-gray-600' : ' bg-white border-gray-400 border-dashed'
					}`}
					onClick={() => {
						if (!!documentState.userWriterSelections.writingStyle?.longDescription) {
							dispatch(
								setUserWriterSelections({
									docID: docID,
									userWriterSelections: {
										writingStyle: {
											...documentState.userWriterSelections.writingStyle,
											generalSelected: false,
											name: 'Custom',
										},
									},
								})
							)
						} else {
							setModalOpen(true)
						}
					}}
				>
					<div className="flex gap-1 justify-between w-full mb-1 items-start">
						<div className="flex flex-col gap-1 w-full">
							<div className="flex gap-1 items-center w-full">
								{customWritingStyleGenerated && TbSparkles({})}
								<div className="flex items-center justify-between w-full">
									<div className="text-lg font-bold">
										{customWritingStyleGenerated ? 'Your Persona' : 'Custom Writing Persona'}
									</div>
									{LuAudioWaveform({ className: 'w-5 h-5 text-gray-400' })}
								</div>
							</div>
							<div className="text-sm text-gray-800">
								{customWritingStyleGenerated
									? documentState.userWriterSelections.writingStyle?.description
									: 'Paste in a sample text to personalize your writing persona.'}
							</div>
						</div>
						{customWritingStyleGenerated && !generalSelected && (
							<Button
								type="outline"
								emphasize={!customWritingStyleGenerated}
								className="bg-white text-sm !py-1 hidden md:block ml-2"
								onClick={() => {
									setModalOpen(true)
								}}
							>
								Edit
							</Button>
						)}
					</div>
					{customWritingStyleGenerated && !generalSelected && (
						<Button
							type="outline"
							emphasize={!customWritingStyleGenerated}
							className="bg-white text-sm !py-1 mt-2 block md:hidden"
							onClick={() => {
								setModalOpen(true)
							}}
						>
							Edit Style
						</Button>
					)}
				</div>
				<div className={`grid md:grid-cols-[1fr,1fr] md:gap-6 gap-2 justify-center flex-wrap md:mt-2`}>
					{personas.map((persona) => {
						const selected = documentState.userWriterSelections.writingStyle?.name === persona.id
						return (
							<div
								className={`cursor-pointer border-2 px-6 py-4 rounded-lg flex flex-col hover:bg-gray-50 ${
									selected ? 'bg-gray-100 border-gray-600' : ' bg-white border-gray-300'
								}`}
								onClick={() => {
									dispatch(
										setUserWriterSelections({
											docID: docID,
											userWriterSelections: {
												...documentState.userWriterSelections,
												writingStyle: {
													...documentState.userWriterSelections.writingStyle,
													generalSelected: true,
													name: persona.id,
												},
											},
										})
									)
								}}
							>
								<div className="flex gap-2 items-center justify-between">
									<div className="text-lg font-bold">{persona.title}</div>
									<div className="text-sm text-gray-400">{persona.icon({ className: 'w-6 h-6' })}</div>
								</div>
								<div className="text-sm text-gray-800 mt-0.5">{persona.description}</div>
							</div>
						)
					})}
				</div>
			</>
		)
	}

	return (
		<>
			<CustomWritingStyleModal docID={docID} modalOpen={modalOpen} setModalOpen={setModalOpen} />
			<div className="flex flex-col gap-4 mt-6">
				{!omitHeader && <div className={`text-xl font-semibold `}>Writing Style</div>}
				<div className={`grid md:grid-cols-[1fr,1fr] md:gap-8 gap-2 justify-center flex-wrap`}>
					<div
						className={`cursor-pointer border-2 px-6 py-4 rounded-lg flex flex-col hover:shadow-md ${
							generalSelected ? 'bg-gray-100 border-gray-600' : ' bg-white border-gray-300'
						}`}
						onClick={() => {
							dispatch(
								setUserWriterSelections({
									docID: docID,
									userWriterSelections: {
										...documentState.userWriterSelections,
										writingStyle: {
											...documentState.userWriterSelections.writingStyle,
											generalSelected: true,
										},
									},
								})
							)
						}}
					>
						<div className="text-xl font-semibold">Standard</div>
						<div className="text-sm text-gray-800 mt-0.5">College reading level with an academic tone.</div>
					</div>
					<div
						className={`cursor-pointer border-2 px-6 py-4 rounded-lg flex flex-col items-start hover:shadow-md ${
							!generalSelected ? 'bg-gray-100 border-gray-600' : ' bg-white border-gray-300'
						}`}
						onClick={() => {
							if (customWritingStyleGenerated) {
								dispatch(
									setUserWriterSelections({
										docID: docID,
										userWriterSelections: {
											writingStyle: {
												...documentState.userWriterSelections.writingStyle,
												generalSelected: false,
											},
										},
									})
								)
							} else {
								setModalOpen(true)
							}
						}}
					>
						<div className="flex gap-1 justify-between w-full mb-1 items-start">
							<div className="flex flex-col gap-1">
								<div className="flex gap-1 items-center">
									{customWritingStyleGenerated && TbSparkles({})}
									<div className="text-xl font-semibold">
										{customWritingStyleGenerated ? 'Your Style' : 'Custom Writing Style'}
									</div>
								</div>
								<div className="text-sm text-gray-800">
									{customWritingStyleGenerated
										? documentState.userWriterSelections.writingStyle?.description
										: 'Paste in a sample text to personalize your writing style.'}
								</div>
							</div>
							{customWritingStyleGenerated && !generalSelected && (
								<Button
									type="outline"
									emphasize={!customWritingStyleGenerated}
									className="bg-white text-sm !py-1 hidden md:block"
									onClick={() => {
										setModalOpen(true)
									}}
								>
									Edit
								</Button>
							)}
						</div>
						{customWritingStyleGenerated && !generalSelected && (
							<Button
								type="outline"
								emphasize={!customWritingStyleGenerated}
								className="bg-white text-sm !py-1 mt-2 block md:hidden"
								onClick={() => {
									setModalOpen(true)
								}}
							>
								Edit Style
							</Button>
						)}
					</div>
				</div>
			</div>
		</>
	)
}

export default WritingStyleSelection
